import React, { useState } from "react";
import "./TauriDocuments.css";
import TauriImage from "../TauriImage/TauriImage";
import appURL from '../../appURL.json';
import { updateRecord } from "../../localDB";
import { FileStatus } from "../../visualizations/visualizations";
import { Spinner } from "react-bootstrap";
import OpalAlertModal from "../OpalAlertModal/OpalAlertModal";

const fileIcons = {
    pdf: "/Images/pdf.png",
    doc: "/Images/word.png",
    docx: "/Images/word.png",
    xls: "/Images/excel.png",
    xlsx: "/Images/excel.png",
    xlsb: "/Images/excel.png",
    csv: "/Images/excel.png",
    ppt: "/Images/powerpoint.png",
    pptx: "/Images/powerpoint.png",
    txt: "/Images/text.png",
    jpg: "/Images/image-icon.png",
    jpeg: "/Images/image-icon.png",
    png: "/Images/image-icon.png",
    gif: "/Images/image-icon.png",
    bmp: "/Images/image-icon.png",
    svg: "/Images/image-icon.png",
    webp: "/Images/image-icon.png",
    mp4: "/Images/video-icon.png",
    avi: "/Images/video-icon.png",
    mkv: "/Images/video-icon.png",
    mov: "/Images/video-icon.png",
    wmv: "/Images/video-icon.png",
    flv: "/Images/video-icon.png",
    mp3: "/Images/audio-icon.png",
    wav: "/Images/audio-icon.png",
    ogg: "/Images/audio-icon.png",
    flac: "/Images/audio-icon.png",
    zip: "/Images/zip-icon.png",
    rar: "/Images/zip-icon.png",
    tar: "/Images/zip-icon.png",
    gz: "/Images/zip-icon.png",
    "7z": "/Images/zip-icon.png",
    js: "/Images/code-icon.png",
    json: "/Images/code-icon.png",
    html: "/Images/code-icon.png",
    css: "/Images/code-icon.png",
    php: "/Images/code-icon.png",
    py: "/Images/code-icon.png",
    java: "/Images/code-icon.png",
    c: "/Images/code-icon.png",
    cpp: "/Images/code-icon.png",
    ts: "/Images/code-icon.png",
    default: "/Images/file.png",
};

/*
 * File types that the browser can normally open in a new tab.
 */
const viewableExtensions = new Set([
    ".pdf",

    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".svg",
    ".webp",

    ".mp4",
    ".webm",
    ".ogg",
    ".avi",
    ".mkv",
    ".mov",
    ".wmv",
    ".flv",

    ".mp3",
    ".wav",
    ".flac",

    ".txt",
    ".html",
    ".css",
    ".js",
    ".json",
]);

const TauriDocuments = ({
    doc,
    handleDeleteClick,
    handleClick, 
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [status, setStatus] = useState(doc?.Status);
    const [tauriDocumentAction, setTauriDocumentAction] = useState(null);
    const [showMessage, setShowMessage] = useState(false);

    const isViewable = viewableExtensions.has(doc?.Extension);
    const icon = fileIcons[doc?.Extension?.replace(".", "")] || fileIcons.default;

    if (!doc) {
        return <div className="no-documents">No document available</div>;
    }

    const handleDownloadClick = async () => {
        if(status == FileStatus.downloaded) {
            setTauriDocumentAction({
                title: "Message",
                bodyText: "File is already downloaded.",
                onConfirm: () => setShowMessage(false),
                confirmText: "OK"
            });
            setShowMessage(true);
            return;
        }

        if (!doc?.BlobData) {
            console.error("BlobData is missing for this document.");
            return;
        }

        try {
            setIsDownloading(true);
            const url = URL.createObjectURL(doc.BlobData);

            const a = document.createElement("a");
            a.href = url;

            const fileName = `${doc?.FileName || "download"}`;
            a.download = fileName;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            if (doc?.ID == null) {
                console.error("Cannot update file: missing ID", doc);
                return;
            }

            const updatedDoc = {
                ...doc,
                Status: FileStatus.downloaded
            };

            await updateRecord("file", updatedDoc)
            setStatus(FileStatus.downloaded);

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);

        } catch (error) {
            console.error("Failed to download BlobData:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDocumentClick = () => {
        if(!doc?.BlobData){
            console.error("BlobData is missing for this document.");
            return;
        }

        if(isViewable){
            handleClick(doc);
        } else {
            handleDownloadClick();
        }
    }

    return (
        <div className="documents-container d-flex flex-column">
            <div
                className="document-item d-flex align-items-center gap-2"
                onClick={handleDocumentClick}
            >
                <TauriImage
                    localImage={icon}
                    alt="Document Icon"
                    className="document-icon"
                />
                <div className="document-info d-flex flex-column">
                    <div className="document-title">{doc?.FileName}</div>
                    <div className="document-meta d-flex flex-wrap align-items-center">
                        {doc?.Status === 0 ? (
                            <div className="document-status">Awaiting Approval</div>
                        ) : (
                            <div className="document-dates">
                                {doc?.UploadedDate
                                    ? new Date(doc?.UploadedDate).toLocaleString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                    })
                                    : ""}
                            </div>
                        )}

                        {/* ✅ download icon only if file is not yet downloaded */}
                        {(status !== FileStatus.downloaded && !isViewable) && (
                            isDownloading ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="ms-2"
                                />
                            ) : (
                                <TauriImage
                                    localImage={"/Images/mdi--download.svg"}
                                    alt="Download Document"
                                    className="download-icon ms-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadClick();
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
            {tauriDocumentAction && (
                <OpalAlertModal
                    show={showMessage}
                    onHide={() => setShowMessage(false)}
                    title={tauriDocumentAction.title}
                    bodyText={tauriDocumentAction.bodyText}
                    onConfirm={tauriDocumentAction.onConfirm}
                    confirmButtonText={tauriDocumentAction.confirmText}
                    cancelButtonText=""
                    modalType="message"
                />
            )}
        </div>
    );
};

export default TauriDocuments;
